import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getEventsByAdherent } from "../../../../services/eventServices";
import { getAdherentsByUser } from "../../../../services/adherentServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_STATISTIQUES_FILTERS,
  buildStatistiquesData,
  countActiveStatistiquesFilters,
  dedupeStatistiqueEvents,
  filterStatistiqueEvents,
  getAvailableSeasons,
  sortStatistiqueEvents,
} from "../utils/statistiquesJudokaUtils";

export function useStatistiquesJudokaPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_STATISTIQUES_FILTERS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const adherents = await getAdherentsByUser();

      if (!Array.isArray(adherents) || adherents.length === 0) {
        setEvents([]);

        notificationRef.current?.(
          "Aucun adhérent n'est rattaché à votre compte.",
          "error",
        );
        return;
      }

      const adherentId = adherents[0]?.id;

      if (!adherentId) {
        setEvents([]);

        notificationRef.current?.(
          "Impossible d'identifier l'adhérent connecté.",
          "error",
        );
        return;
      }

      const response = await getEventsByAdherent(adherentId);
      const safeEvents = Array.isArray(response?.Events) ? response.Events : [];
      const uniqueEvents = dedupeStatistiqueEvents(safeEvents);

      setEvents(sortStatistiqueEvents(uniqueEvents, "dateDesc"));

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Statistiques actualisées.", "success");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des statistiques.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData({ silent: true });
  }, [fetchData]);

  const filteredEvents = useMemo(() => {
    return filterStatistiqueEvents(events, filters);
  }, [events, filters]);

  const { stats, chartData } = useMemo(() => {
    return buildStatistiquesData(filteredEvents);
  }, [filteredEvents]);

  const seasons = useMemo(() => {
  return getAvailableSeasons(events);
}, [events]);

  const activeFiltersCount = useMemo(() => {
    return countActiveStatistiquesFilters(filters);
  }, [filters]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_STATISTIQUES_FILTERS);
  }, []);

  return {
    events,
    filteredEvents,
    filters,
    loading,
    refreshing,
    stats,
    chartData,
    seasons,
    activeFiltersCount,
    fetchData,
    handleFilterChange,
    resetFilters,
  };
}